// Whether the times in the offer list still match the times the live link
// actually holds.
//
// The offer list above the link block stays editable after a link has gone
// out, and nothing connected the two. So the office could add a fourth time,
// look at a list of four, and believe the patient could take any of them —
// while the link out there holds three and the patient's page shows three.
// The list they are reading is not the promise that was made.
//
// This only reports. Re-minting is a decision, not something to do behind
// somebody's back: the link they already sent stops working the moment a new
// one is made, and only they know whether the patient has it open.

export interface TimeRef {
  date?: string | null;
  time?: string | null;
}

export interface LinkDrift {
  drifted: boolean;
  added: number;
  removed: number;
  message: string | null;
}

const NONE: LinkDrift = { drifted: false, added: 0, removed: 0, message: null };

// Seconds are noise: the holds table answers 10:00, the availability grid
// sometimes 10:00:00, and they are the same time.
const key = (slot: TimeRef): string =>
  `${String(slot?.date || '').trim()} ${String(slot?.time || '').trim().slice(0, 5)}`;

const count = (n: number, verb: string): string => (n === 1 ? `one has been ${verb}` : `${n} have been ${verb}`);

export function describeLinkDrift(
  held: TimeRef[] | null | undefined,
  offered: TimeRef[] | null | undefined,
): LinkDrift {
  const heldKeys = new Set((held || []).map(key).filter((k) => k.trim()));
  // An open link holds nothing on purpose. It cannot drift, and warning about
  // it would be telling the office off for the thing they chose to do.
  if (heldKeys.size === 0) return NONE;

  const offeredKeys = new Set((offered || []).map(key).filter((k) => k.trim()));
  const added = Array.from(offeredKeys).filter((k) => !heldKeys.has(k)).length;
  const removed = Array.from(heldKeys).filter((k) => !offeredKeys.has(k)).length;
  if (!added && !removed) return NONE;

  const parts = [
    ...(added ? [count(added, 'added')] : []),
    ...(removed ? [count(removed, 'removed')] : []),
  ];
  return {
    drifted: true,
    added,
    removed,
    // Not "the times above": the chosen list is rendered BELOW this block, so
    // that phrase pointed staff at the availability grid instead. Naming the
    // thing beats pointing at it — the layout can move again.
    message:
      `The times you have chosen no longer match the link that is out: ${parts.join(', ')}. ` +
      'Send a new link to hold what you have chosen now.',
  };
}
